import { useMutation } from "@apollo/client/react";

import { useCurrentUser } from "./use-current-user";
import SET_ACCOUNT_PREFERENCE from "../graphql/mutations/SetAccountPreference";
import { getPreferenceValue } from "../utils/preferences";

export type PimsExportFormat =
  | "plain_text"
  | "markdown"
  | "rich_text";

export const VALID_FORMATS: PimsExportFormat[] = [
  "plain_text",
  "markdown",
  "rich_text",
];

export const PIMS_EXPORT_FORMAT_KEY = "pims-export-format";
export const PIMS_EXPORT_FORMAT_DEFAULT: PimsExportFormat =
  "markdown";

function parsePreferencesFormat(
  preferences: unknown,
): PimsExportFormat {
  const raw = getPreferenceValue(preferences, PIMS_EXPORT_FORMAT_KEY);
  return VALID_FORMATS.includes(raw as PimsExportFormat)
    ? (raw as PimsExportFormat)
    : PIMS_EXPORT_FORMAT_DEFAULT;
}

export function usePimsExportFormat(): PimsExportFormat {
  const { user } = useCurrentUser();
  return parsePreferencesFormat(user?.account?.preferences);
}

export function useSetPimsExportFormat() {
  const { user, refetch, loading: userLoading } = useCurrentUser();
  const [mutate, { loading: mutationLoading }] = useMutation(
    SET_ACCOUNT_PREFERENCE,
  );
  const currentFormat = parsePreferencesFormat(
    user?.account?.preferences,
  );
  const loading = userLoading || mutationLoading;

  const setFormat = async (
    value: PimsExportFormat,
  ): Promise<boolean> => {
    if (!VALID_FORMATS.includes(value)) return false;
    if (value === currentFormat || loading) return false;
    const result = await mutate({
      variables: {
        key: PIMS_EXPORT_FORMAT_KEY,
        value,
      },
    });
    if (!result.data?.setAccountPreference?.ok) {
      throw new Error("Failed to save account preference");
    }
    await refetch();
    return true;
  };

  return { setFormat, loading };
}
